import type { ReactElement, CSSProperties } from "react";

/**
 * Wordmark size options
 */
type WordmarkSize = "sm" | "md" | "lg";

/**
 * Wordmark variant options
 */
type WordmarkVariant = "default" | "light";

/**
 * Wordmark component props
 */
interface WordmarkProps {
  size?: WordmarkSize;
  variant?: WordmarkVariant;
  className?: string;
}

/**
 * Size style configuration
 */
interface SizeStyle {
  main: string;
  sparkle: string;
}

/**
 * Color scheme configuration
 */
interface ColorScheme {
  main: string;
  accent: string;
}

const SIZE_STYLES: Record<WordmarkSize, SizeStyle> = {
  sm: { main: "1.125rem", sparkle: "0.75rem" },
  md: { main: "1.375rem", sparkle: "0.875rem" },
  lg: { main: "1.625rem", sparkle: "1rem" },
};

const COLOR_SCHEMES: Record<WordmarkVariant, ColorScheme> = {
  default: {
    main: "var(--st-text-heading)",
    accent: "var(--st-primary)",
  },
  light: {
    main: "#FFFFFF",
    accent: "#FFB088",
  },
};

/**
 * Tellor Wordmark Component
 * Stylized text logo with storybook vibes
 */
const Wordmark = ({
  size = "md",
  variant = "default",
  className = "",
}: WordmarkProps): ReactElement => {
  const styles = SIZE_STYLES[size] || SIZE_STYLES.md;
  const colorScheme = COLOR_SCHEMES[variant] || COLOR_SCHEMES.default;

  const mainStyle: CSSProperties = {
    fontFamily: "Chewy, cursive",
    fontSize: styles.main,
    color: colorScheme.main,
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  return (
    <span
      className={`inline-flex items-center leading-none ${className}`}
    >
      {/* "Tellor ✦" - main brand with sparkle */}
      <span style={mainStyle}>
        Tellor
        <span style={{ color: colorScheme.accent, fontSize: styles.sparkle }}>
          ✦
        </span>
      </span>
    </span>
  );
};

export default Wordmark;
